import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    title: "1. Identidad del Responsable",
    body: (
      <>
        <p>
          <strong>TI Datos y Sistemas Wok Center</strong> (en adelante, “Wok
          Center”) es responsable del tratamiento, uso y protección de los datos
          personales que usted nos proporcione a través del formulario de contacto
          de este sitio o por otros medios físicos o electrónicos.
        </p>
        <p>
          Para cualquier asunto relacionado con este Aviso de Privacidad puede
          escribir al área de Operaciones a través del formulario de contacto
          disponible en este mismo sitio web.
        </p>
      </>
    ),
  },
  {
    title: "2. Datos personales que recabamos",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>Nombre completo.</li>
        <li>Nombre de la empresa u organización.</li>
        <li>Correo electrónico de contacto.</li>
        <li>Teléfono de contacto.</li>
        <li>
          Requerimiento o descripción del proyecto que el usuario decida compartir
          de forma voluntaria.
        </li>
      </ul>
    ),
  },
  {
    title: "3. Finalidades del tratamiento",
    body: (
      <>
        <p>Tratamos sus datos personales para las siguientes finalidades:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Atender su solicitud de contacto, cotización o información sobre
            nuestros servicios.
          </li>
          <li>
            Asignarle un asesor tecnológico que se ponga en contacto con usted en
            un plazo no mayor a 24 horas hábiles.
          </li>
          <li>
            Elaborar propuestas comerciales, cotizaciones y planes de trabajo
            personalizados.
          </li>
          <li>Dar cumplimiento a obligaciones contractuales y/o legales.</li>
          <li>
            De manera secundaria y previo consentimiento expreso: enviarle
            comunicaciones comerciales sobre productos, servicios o eventos
            relacionados.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "4. Derechos ARCO",
    body: (
      <>
        <p>
          Usted tiene derecho a conocer qué datos personales tenemos de usted, para
          qué los utilizamos y las condiciones de uso que les damos
          (<strong>Acceso</strong>). Asimismo, puede solicitar la corrección de
          información inexacta o incompleta (<strong>Rectificación</strong>); que
          la eliminemos cuando considere que no se requiere para alguna de las
          finalidades señaladas (<strong>Cancelación</strong>); u oponerse al uso
          de sus datos personales para fines específicos (<strong>Oposición</strong>).
        </p>
        <p>
          Para ejercer sus derechos ARCO puede contactarnos a través del formulario
          de contacto del sitio, indicando claramente su solicitud y adjuntando
          identificación oficial.
        </p>
      </>
    ),
  },
  {
    title: "5. Transferencia de datos",
    body: (
      <p>
        Sus datos personales no serán compartidos con terceros, salvo aquellas
        transferencias necesarias para dar cumplimiento al servicio contratado, a
        proveedores tecnológicos que actúan como encargados del tratamiento bajo
        acuerdos de confidencialidad, o cuando exista obligación legal.
      </p>
    ),
  },
  {
    title: "6. Medios de seguridad",
    body: (
      <p>
        Implementamos medidas de seguridad administrativas, técnicas y físicas
        razonables para proteger sus datos personales contra daño, pérdida,
        alteración, destrucción o uso no autorizado, incluyendo cifrado en
        tránsito (HTTPS) y controles de acceso a nuestros sistemas internos.
      </p>
    ),
  },
  {
    title: "7. Uso de cookies y tecnologías similares",
    body: (
      <p>
        Este sitio puede utilizar cookies y tecnologías similares para mejorar su
        experiencia de navegación y obtener métricas anónimas de uso. Usted puede
        deshabilitarlas desde la configuración de su navegador en cualquier
        momento.
      </p>
    ),
  },
  {
    title: "8. Cambios al Aviso de Privacidad",
    body: (
      <p>
        Nos reservamos el derecho de actualizar el presente Aviso de Privacidad en
        cualquier momento. Cualquier modificación será publicada en este mismo
        sitio web. Le recomendamos revisarlo periódicamente.
      </p>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <main data-testid="privacy-page" className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <section className="pt-32 md:pt-40 pb-20">
        <div className="px-6 md:px-12 lg:px-16 max-w-[960px] mx-auto">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#525252] hover:text-[#0047FF] mb-12"
          >
            <ArrowLeft size={14} />
            Volver al inicio
          </Link>

          <span className="eyebrow">Documento legal</span>
          <h1 className="display-2 mt-5 mb-6">Aviso de Privacidad</h1>
          <p className="text-[#525252] text-base md:text-lg leading-relaxed mb-4 max-w-2xl">
            En <strong>TI Datos y Sistemas Wok Center</strong> protegemos sus datos
            personales conforme a la legislación aplicable en materia de
            protección de datos.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3] border-t border-[#E5E5E5] pt-6 mb-16">
            Documento vigente
          </p>

          <div className="space-y-12">
            {SECTIONS.map((s, idx) => (
              <article
                key={s.title}
                data-testid={`privacy-section-${idx}`}
                className="border-t border-[#E5E5E5] pt-10"
              >
                <h2 className="font-display text-2xl md:text-[28px] font-bold tracking-tight mb-5">
                  {s.title}
                </h2>
                <div className="text-[#525252] text-[15.5px] leading-relaxed space-y-4 max-w-2xl">
                  {s.body}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-20 bg-[#0A0A0A] text-white p-10 md:p-14">
            <span className="eyebrow eyebrow-dark">¿Tienes dudas?</span>
            <h3 className="display-3 mt-3 mb-4 text-white">
              Contáctanos a través del formulario.
            </h3>
            <p className="text-[#A3A3A3] mb-8 max-w-xl">
              Si requieres ejercer tus derechos ARCO o tienes cualquier consulta
              sobre el tratamiento de tus datos personales, escríbenos desde el
              formulario de contacto.
            </p>
            <Link
              to="/#contacto"
              data-testid="privacy-contact-cta"
              className="inline-flex items-center gap-3 bg-white text-[#0A0A0A] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#0047FF] hover:text-white transition-colors"
            >
              Ir al formulario de contacto →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
